'use client';

import { shallowEqual, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NostoContext } from './NostoContext';
import { useCartContext } from '../cart/CartContext';
import Cookies from 'js-cookie';

const NOSTO_SESSION_PARAMS_COOKIE = 'nostoSessionParams';

const getCustomer = (customer, cart, formData) => {
  const address = cart?.shippingAddress || cart?.billingAddress;
  const email = formData?.email || customer?.email || cart?.email;
  const firstName = formData?.firstName || customer?.firstName || address?.firstName;
  const lastName = formData?.lastName || customer?.lastName || address?.lastName;
  const reference = customer?.accountId;

  return {
    email,
    first_name: firstName,
    last_name: lastName,
    customer_reference: reference,
    newsletter: formData?.marketingComms,
  };
};

export const NostoProvider = ({ currentVariation = '', multiCurrency = false, children }) => {
  // eslint-disable-next-line no-unused-vars
  const [merchandisedCategory, setMerchandisedCategory] = useState(null);
  const [hasNostoJs, setHasNostoJs] = useState(false);
  const [hasNostoSessionParams, setHasNostoSessionParams] = useState(false);
  const { cartData, parseCardDataForNosto } = useCartContext();
  const [pageView, setPageView] = useState('');
  const [loading, setLoading] = useState(false);
  const [placements, setPlacements] = useState([]);
  const [placementData, setPlacementData] = useState({});

  const { account, ssr, triggerRoute } = useSelector(
    (state) => ({
      account: state.app.context.session.account,
      ssr: state.renderContext.serverSideRendering,
      triggerRoute: state['demo-common'].triggerRoute,
    }),
    shallowEqual,
  );

  const nostoJs = !ssr && window?.nostojs ? window?.nostojs : null;

  const initiateSession = useCallback(() => {
    hasNostoJs &&
      typeof nostoJs === 'function' &&
      nostoJs((api) => {
        api
          .defaultSession()
          .setCart(cartData?.cart?.lineItems ? { items: parseCardDataForNosto(cartData) } : undefined)
          .viewOther()
          .load();
      });
  }, [hasNostoJs, cartData, account]);

  const getSearchSessionParams = useCallback(() => {
    if (!hasNostoJs && typeof nostoJs !== 'function') return;

    try {
      return nostoJs((api) => {
        return api.getSearchSessionParams().then((response) => {
          setHasNostoSessionParams(true);
          Cookies.set(NOSTO_SESSION_PARAMS_COOKIE, JSON.stringify(response), {
            expires: 30 / 1440, // 30 minutes
            secure: true,
          });

          return response;
        });
      });
    } catch (error) {
      console.error('Error fetching nosot search session params:', error);
      setHasNostoSessionParams(false);
    }
  }, [hasNostoJs, cartData, hasNostoSessionParams]);

  const getProductRecommendations = useCallback(() => {
    typeof nostoJs === 'function' &&
      nostoJs((api) => {
        api
          .defaultSession()
          .viewFrontPage()
          .setPlacements(placements)
          .setCart(cartData?.cart?.lineItems ? { items: parseCardDataForNosto(cartData) } : undefined)
          .load({ skipPageViews: true, skipEvents: true })
          .then((res) => {
            setPlacementData(res?.recommendations);
          });
      });
  }, [hasNostoJs, nostoJs, placements, setLoading, setPlacementData]);

  const getCartRecommendations = useCallback(() => {
    typeof nostoJs === 'function' &&
      nostoJs((api) => {
        api
          .defaultSession()
          .viewCart()
          .setPlacements(placements)
          .setCart(cartData?.cart?.lineItems ? { items: parseCardDataForNosto(cartData) } : undefined)
          .load({ skipPageViews: true, skipEvents: true })
          .then((res) => {
            setPlacementData(res?.recommendations);
          });
      });
  }, [hasNostoJs, nostoJs, placements, setLoading, setPlacementData]);

  const getMiniCartProducts = useCallback(
    (placement, setProducts) => {
      typeof nostoJs === 'function' &&
        nostoJs((api) => {
          api
            .defaultSession()
            .viewFrontPage()
            .setPlacements([placement])
            .load()
            .then((data) => {
              setProducts(data?.recommendations?.[placement]);
            });
        });
    },
    [hasNostoJs, nostoJs, setLoading, setPlacementData],
  );

  const getProductPageRecommendations = useCallback(
    (productId, placements) => {
      typeof nostoJs === 'function' &&
        nostoJs((api) => {
          api
            .defaultSession()
            .viewProduct(productId)
            .setCart(cartData?.cart?.lineItems ? { items: parseCardDataForNosto(cartData) } : undefined)
            .setPlacements(placements)
            .load({ skipPageViews: true, skipEvents: true })
            .then((data) => {
              setPlacementData(data?.recommendations);
            });
        });
    },
    [hasNostoJs, nostoJs, placements, cartData, setLoading, setPlacementData],
  );

  const nostoCategoryView = useCallback(
    (path) => {
      typeof nostoJs === 'function' &&
        nostoJs((api) => {
          api
            .defaultSession()
            .viewCategory(path)
            .setCart(cartData?.cart?.lineItems ? { items: parseCardDataForNosto(cartData) } : undefined)
            .load();
        });
    },
    [hasNostoJs, nostoJs, cartData],
  );

  const nostoCategoryLandOnPage = useCallback(
    (target) => {
      if (!target) return;
      typeof nostoJs === 'function' &&
      nostojs((api) =>
        api
          .recordAttribution({
            type: 'vc',
            target,
          })
          .done(),
      );
    },
    [hasNostoJs, nostoJs, cartData],
  );

  const nostoSearchLandOnPage = useCallback(
    (target) => {
      if (!target) return;
      typeof nostoJs === 'function' &&
      nostojs((api) =>
        api
          .recordAttribution({
            type: 'is',
            target: target,
            refType: 'os',
          })
          .done(),
      );
    },
    [hasNostoJs, nostoJs, cartData],
  );

  const nostoSearchView = useCallback(
    (term, placement) => {
      if (!term || !placement) return;
      typeof nostoJs === 'function' &&
        nostoJs((api) => {
          api
            .defaultSession()
            .viewSearch(term)
            .setPlacements([placement])
            .setCart(cartData?.cart?.lineItems ? { items: parseCardDataForNosto(cartData) } : undefined)
            .load()
            .then((res) => {
              setPlacementData(res?.recommendations);
            });
        });
    },
    [hasNostoJs, nostoJs, cartData],
  );

  const nostoUpdateCart = useCallback(
    (data) => {
      typeof nostoJs === 'function' &&
        nostoJs((api) => {
          api
            .defaultSession()
            .setCart(cartData?.cart?.lineItems ? { items: parseCardDataForNosto(data) } : undefined)
            .viewCart()
            .update();
        });
    },
    [hasNostoJs, nostoJs, cartData],
  );

  const nostoUpdateCustomer = useCallback(
    (formData) => {
      typeof nostoJs === 'function' &&
        nostoJs((api) => {
          api
            .defaultSession()
            .setCustomer(getCustomer(account, cartData?.cart, formData) || undefined)
            .viewCart()
            .update();
        });
    },
    [hasNostoJs, nostoJs, cartData],
  );

  const nostoCheckoutView = useCallback(() => {
    typeof nostoJs === 'function' &&
      nostoJs((api) => {
        api
          .defaultSession()
          .setCart(cartData?.cart?.lineItems ? { items: parseCardDataForNosto(cartData) } : undefined)
          .viewCart()
          .load();
      });
  }, [hasNostoJs, nostoJs, cartData]);

  const nostoAddToCart = useCallback(
    ({ productId, placement }) => {
      if (!placement) return;
      typeof nostoJs === 'function' &&
        nostoJs((api) => {
          api.defaultSession().recordAttribution('cp', productId, placement).done();
        });
    },
    [hasNostoJs, nostoJs, cartData],
  );

  const nostoProductClick = useCallback(
    (productId, placement, source) => {
      typeof nostoJs === 'function' &&
        nostoJs((api) => {
          if (source) {
            api
              .defaultSession()
              .recordAttribution('VP', productId, source)
              .recordAttribution('src', 'cmp', source)
              // .recordAttribution('cp', productId, source)
              .done();
            return;
          }

          api.defaultSession().recordAttribution('VP', productId, placement).done();
        });
    },
    [hasNostoJs, nostoJs, cartData],
  );

  const nostoOrderSuccessView = useCallback(
    (order) => {
      const { items, email, shippingAddress, billingAddress, orderId, payments, orderState } = order;

      const { firstName, lastName } = shippingAddress || billingAddress;
      const { paymentMethod } = payments?.[0] || { paymentMethod: '' };

      typeof nostoJs === 'function' &&
        nostoJs((api) => {
          api
            .defaultSession()
            .addOrder({
              info: {
                order_number: orderId,
                email: email,
                order_status: orderState,
                first_name: firstName,
                last_name: lastName,
                type: 'order',
                paymentMethod,
              },
              items: items,
            })
            .load();
        });
    },
    [hasNostoJs, nostoJs, cartData],
  );

  const nostoSearchOnChange = useCallback(
    ({ type = 'autocomplete', searchRequest, searchResults, options = {} }) => {
      if (!searchRequest || !searchResults?.count) return;

      typeof nostoJs === 'function' &&
        nostoJs((api) =>
          api.recordSearch(
            type,
            {
              ...searchRequest,
            },
            {
              products: {
                categoryId: null,
                categoryPath: null,
                collapse: null,
                fuzzy: false,
                from: searchResults.from,
                hits: searchResults?.products,
                total: searchResults.total,
                size: searchResults.count,
              },
            },
            options,
          ),
        );
    },
    [hasNostoJs, nostoJs],
  );

  const nostoRecordSearchSubmit = useCallback(
    (query) => {
      typeof nostoJs === 'function' &&
      nostojs((api) => {
        if (typeof query === 'string' && query.trim() !== '') {
          api.recordSearchSubmit(query);
        } else {
          console.warn('Skipping recordSearchSubmit: query is invalid or empty', query);
        }
        });
    },
    [hasNostoJs, nostoJs, cartData],
  );

  const nostoRecordSearchClick = useCallback(
    (type = 'autocomplete', hit) => {
      typeof nostoJs === 'function' &&
        nostoJs(async (api) => {
          await api.recordSearchClick(type, hit);
        });
    },
    [hasNostoJs, nostoJs, cartData],
  );

  const nostoRecordSearchAddToCart = useCallback(
    ({ type = 'autocomplete', hit }) => {
      typeof nostoJs === 'function' &&
      nostoJs(async (api) => {
        if (type) {
          await api.recordSearchAddToCart(type, hit);
        } else {
          console.warn('Skipping nostoRecordSearchAddToCart: type or hit are invalid or empty');
        }
      });
    },
    [hasNostoJs, nostoJs, cartData],
  );

  let initiaVariation = currentVariation;

  initiaVariation = multiCurrency ? initiaVariation : '';

  useEffect(() => {
    if (hasNostoJs) {
      return;
    }

    const checkNostoJs = () => {
      if (!ssr && window?.nostojs) {
        setHasNostoJs(true);
      }
    };

    const intervalId = setInterval(checkNostoJs, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [cartData, account, ssr, nostoJs, triggerRoute, pageView]);

  useEffect(() => {
    const receivedPlacements = Object.keys(placementData || {});
    const isLoadedAllPlacements = placements.every((item) => receivedPlacements.includes(item));
    if (isLoadedAllPlacements) {
      return;
    }
    if (pageView && pageView === 'frontpage') {
      getProductRecommendations();
    }
    if (pageView && pageView === 'cart') {
      getCartRecommendations();
    }
  }, [pageView, triggerRoute, placements]);

  const values = useMemo(() => {
    return {
      initiaVariation,
      initiateSession,
      getProductRecommendations,
      placementData,
      hasNostoJs,
      loading,
      setPageView,
      pageView,
      setPlacements,
      placements,
      getProductPageRecommendations,
      getMiniCartProducts,
      nostoCategoryView,
      setMerchandisedCategory,
      nostoSearchView,
      nostoCheckoutView,
      nostoOrderSuccessView,
      nostoUpdateCart,
      nostoUpdateCustomer,
      nostoProductClick,
      nostoAddToCart,
      nostoSearchOnChange,
      getSearchSessionParams,
      nostoRecordSearchSubmit,
      nostoRecordSearchClick,
      hasNostoSessionParams,
      nostoRecordSearchAddToCart,
      nostoCategoryLandOnPage,
      nostoSearchLandOnPage,
    };
  }, [
    initiaVariation,
    initiateSession,
    getProductRecommendations,
    placementData,
    hasNostoJs,
    loading,
    setPageView,
    pageView,
    setPlacements,
    placements,
    getProductPageRecommendations,
    nostoCategoryView,
    setMerchandisedCategory,
    nostoSearchView,
    nostoCheckoutView,
    nostoOrderSuccessView,
    nostoUpdateCart,
    nostoUpdateCustomer,
    nostoProductClick,
    nostoAddToCart,
    nostoSearchOnChange,
    getSearchSessionParams,
    nostoRecordSearchSubmit,
    nostoRecordSearchClick,
    hasNostoSessionParams,
    nostoRecordSearchAddToCart,
    nostoCategoryLandOnPage,
    nostoSearchLandOnPage,
  ]);

  return <NostoContext.Provider value={values}>{children}</NostoContext.Provider>;
};
